import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2, Save, RotateCcw } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";

// Groups for the content editor UI - each group shows as a card with its fields.
const GROUPS = [
  {
    title: "Hero section",
    description: "The top of the homepage — the first thing visitors see.",
    fields: [
      { key: "hero.label", label: "Eyebrow label", type: "text" },
      { key: "hero.headline_1", label: "Headline · line 1", type: "text" },
      { key: "hero.headline_2", label: "Headline · line 2 (navy)", type: "text" },
      { key: "hero.blurb", label: "Intro blurb", type: "textarea", rows: 4 },
      { key: "hero.quote_label", label: "Portrait quote label", type: "text" },
      { key: "hero.quote", label: "Portrait quote", type: "textarea", rows: 5 },
      { key: "hero.stat_1_number", label: "Stat 1 · number (e.g. 60)", type: "text" },
      { key: "hero.stat_1_label", label: "Stat 1 · label (e.g. minute sessions)", type: "text" },
      { key: "hero.stat_2_number", label: "Stat 2 · number (e.g. 2)", type: "text" },
      { key: "hero.stat_2_label", label: "Stat 2 · label (e.g. locations)", type: "text" },
      { key: "hero.stat_3_number", label: "Stat 3 · number (e.g. Online)", type: "text" },
      { key: "hero.stat_3_label", label: "Stat 3 · label (e.g. available)", type: "text" },
    ],
  },
  {
    title: "Meet Jesse",
    description: "About section with Jesse's photo and credentials.",
    fields: [
      { key: "meet_jesse.label", label: "Eyebrow label", type: "text" },
      { key: "meet_jesse.headline", label: "Headline", type: "text" },
      { key: "meet_jesse.subtitle", label: "Subtitle", type: "text" },
      { key: "meet_jesse.quote", label: "Pull-quote", type: "textarea", rows: 2 },
      { key: "meet_jesse.p1", label: "Paragraph 1", type: "textarea", rows: 6 },
      { key: "meet_jesse.p2", label: "Paragraph 2", type: "textarea", rows: 4 },
      { key: "meet_jesse.personal", label: "Personal note (italic)", type: "textarea", rows: 4 },
      { key: "credentials.card_1_title", label: "Credential 1 · title", type: "text" },
      { key: "credentials.card_1_sub", label: "Credential 1 · subtitle", type: "text" },
      { key: "credentials.card_2_title", label: "Credential 2 · title", type: "text" },
      { key: "credentials.card_2_sub", label: "Credential 2 · subtitle", type: "text" },
    ],
  },
  {
    title: "Section headings",
    description: "Titles and subtitles above each major page section.",
    fields: [
      { key: "areas.headline", label: "Areas of Support · headline", type: "text" },
      { key: "areas.subtitle", label: "Areas of Support · subtitle", type: "textarea", rows: 3 },
      { key: "services.headline", label: "Services · headline", type: "text" },
      { key: "services.subtitle", label: "Services · subtitle", type: "textarea", rows: 3 },
      { key: "locations.headline", label: "Locations · headline", type: "text" },
      { key: "locations.subtitle", label: "Locations · subtitle", type: "textarea", rows: 3 },
      { key: "enquiry.headline", label: "Enquiry · headline", type: "text" },
      { key: "enquiry.subtitle", label: "Enquiry · subtitle", type: "textarea", rows: 4 },
      { key: "groups.headline", label: "Groups callout · headline", type: "text" },
      { key: "groups.body", label: "Groups callout · body", type: "textarea", rows: 4 },
    ],
  },
  {
    title: "Brand & contact",
    description: "Business contact details shown in the footer and on emails.",
    fields: [
      { key: "brand.counsellor_name", label: "Counsellor name", type: "text" },
      { key: "brand.email", label: "Business email", type: "text" },
      { key: "brand.phone", label: "Phone", type: "text" },
      { key: "brand.region", label: "Region / location line", type: "text" },
    ],
  },
  {
    title: "Christ is LORD · Faith section",
    description: "The dark navy section between Meet Jesse and Sessions.",
    fields: [
      { key: "faith.eyebrow", label: "Eyebrow label (leave blank to hide)", type: "text" },
      { key: "faith.headline_1", label: "Headline · first part", type: "text" },
      { key: "faith.headline_2", label: "Headline · emphasis word (italic teal)", type: "text" },
      { key: "faith.tagline", label: "Tagline (italic, under headline)", type: "textarea", rows: 2 },
      { key: "faith.p1", label: "Paragraph 1", type: "textarea", rows: 5 },
      { key: "faith.p2", label: "Paragraph 2", type: "textarea", rows: 5 },
      { key: "faith.p3", label: "Paragraph 3", type: "textarea", rows: 4 },
      { key: "faith.quote", label: "Pull-quote (Scripture)", type: "textarea", rows: 2 },
      { key: "faith.quote_ref", label: "Pull-quote reference", type: "text" },
      { key: "faith.closing", label: "Closing line (smaller, welcoming note)", type: "textarea", rows: 3 },
    ],
  },
  {
    title: "Crisis support intro",
    description: "Wording for the emergency-help section. The list of phone numbers (Lifeline, Beyond Blue, etc.) is fixed for safety — contact Jesse's developer to add or remove a service.",
    fields: [
      { key: "crisis.eyebrow", label: "Eyebrow label", type: "text" },
      { key: "crisis.headline", label: "Headline", type: "text" },
      { key: "crisis.body", label: "Body paragraph", type: "textarea", rows: 4 },
      { key: "crisis.footnote", label: "000 banner line", type: "text" },
    ],
  },
  {
    title: "Booking settings",
    description: "Defaults applied to new bookings (both public and admin-created).",
    fields: [
      { key: "booking.default_buffer_min", label: "Default post-session buffer (minutes) — 0 to disable", type: "text" },
    ],
  },
  {
    title: "Footer quote",
    description: "Italic quote shown at the very bottom of every page.",
    fields: [
      { key: "footer.quote", label: "Quote", type: "textarea", rows: 4 },
      { key: "footer.quote_ref", label: "Quote reference / source", type: "text" },
    ],
  },
];

export default function ContentEditor() {
  const [original, setOriginal] = useState({});
  const [draft, setDraft] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/content");
      setOriginal(data);
      setDraft(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const dirtyKeys = useMemo(
    () => Object.keys(draft).filter((k) => draft[k] !== original[k]),
    [draft, original]
  );

  const save = async () => {
    if (dirtyKeys.length === 0) return;
    setSaving(true);
    const updates = {};
    dirtyKeys.forEach((k) => (updates[k] = draft[k]));
    try {
      await api.patch("/admin/content", { updates });
      toast.success(`Saved ${dirtyKeys.length} change${dirtyKeys.length > 1 ? "s" : ""}`);
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const reset = () => {
    setDraft(original);
    toast.info("Changes reverted");
  };

  const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));

  if (loading)
    return (
      <div className="py-16 flex items-center justify-center text-ink-muted">
        <Loader2 size={18} className="animate-spin mr-2" /> Loading content…
      </div>
    );

  return (
    <div className="space-y-6 pb-24 relative" data-testid="content-editor">
      <div>
        <div className="font-serif text-2xl text-ink">Wording & content</div>
        <div className="text-sm text-ink-soft">
          Edit any text on your website. Changes go live as soon as you hit Save.
        </div>
      </div>

      {GROUPS.map((g) => (
        <div key={g.title} className="bg-cream-soft border border-sand/70 rounded-3xl p-6 md:p-8">
          <div className="mb-6">
            <div className="font-serif text-xl text-ink">{g.title}</div>
            <div className="text-xs text-ink-soft mt-1">{g.description}</div>
          </div>
          <div className="space-y-4">
            {g.fields.map((f) => {
              const val = draft[f.key] ?? "";
              const changed = original[f.key] !== val;
              return (
                <div key={f.key}>
                  <div className="flex items-center justify-between mb-1.5">
                    <label className="text-xs uppercase tracking-[0.18em] text-ink-soft">{f.label}</label>
                    {changed && <span className="text-[10px] uppercase tracking-[0.2em] text-navy">unsaved</span>}
                  </div>
                  {f.type === "textarea" ? (
                    <textarea
                      rows={f.rows || 3}
                      value={val}
                      onChange={(e) => set(f.key, e.target.value)}
                      data-testid={`content-${f.key}`}
                      className={`${inp} resize-none`}
                    />
                  ) : (
                    <input
                      type="text"
                      value={val}
                      onChange={(e) => set(f.key, e.target.value)}
                      data-testid={`content-${f.key}`}
                      className={inp}
                    />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}

      {/* Sticky save bar */}
      <div className="fixed bottom-0 left-0 right-0 z-30 bg-cream/95 backdrop-blur-md border-t border-sand/70 px-6 lg:px-10 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between gap-4">
          <div className="text-sm text-ink-muted">
            {dirtyKeys.length === 0
              ? "No unsaved changes."
              : `${dirtyKeys.length} unsaved change${dirtyKeys.length > 1 ? "s" : ""}.`}
          </div>
          <div className="flex gap-2">
            <button
              onClick={reset}
              disabled={dirtyKeys.length === 0}
              className="inline-flex items-center gap-2 rounded-full border border-ink/20 px-5 py-2.5 text-sm hover:border-navy hover:text-navy disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <RotateCcw size={13} /> Revert
            </button>
            <button
              onClick={save}
              disabled={dirtyKeys.length === 0 || saving}
              data-testid="content-save-button"
              className="inline-flex items-center gap-2 rounded-full bg-navy text-cream px-6 py-2.5 text-sm font-medium hover:bg-navy-deep disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              Save changes
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

const inp = "w-full bg-cream border border-sand/80 rounded-xl px-4 py-2.5 text-ink focus:outline-none focus:border-navy focus:ring-1 focus:ring-navy";
